import { NestApi } from "../../redux/api/axiosInstance";

/* Teams meetings and the Teams OAuth connection are served by the ATS
   (teams-meetings module). They used to come from the separate .NET Recruiter API,
   which is retired. The ATS wraps every JSON response as { success, statusCode, data },
   so each call unwraps it here. */
const unwrap = (body: any) => (body && typeof body === 'object' && 'data' in body && 'statusCode' in body ? body.data : body);

export interface TeamsMeetingInvitePayload {
  organizerEmail: string;
  attendeeEmail: string;
  startUtc: string;
  endUtc: string;
  subject: string;
  description: string;
}

export interface TeamsMeetingResponse {
  // Microsoft Graph Event fields
  id: string;
  subject: string;
  start?: {
    dateTime: string;
    timeZone: string;
  };
  end?: {
    dateTime: string;
    timeZone: string;
  };
  startDateTime?: string;
  endDateTime?: string;
  joinWebUrl?: string;
  onlineMeeting?: {
    joinUrl: string;
  };
  meetingId?: string;
  organizerEmail?: string;
  attendeeEmail?: string;
  startUtc?: string;
  endUtc?: string;
  description?: string;
  meetingUrl?: string;
  joinUrl?: string;
  [key: string]: any;
}

export interface OAuthCallbackParams {
  code: string;
  state: string;
}

export interface TeamsStatusResponse {
  isAuthenticated?: boolean;
  teamsConnected?: boolean;
  valid?: boolean;
  userEmail?: string;
  message?: string;
  status?: string;
  [key: string]: any;
}

export interface TeamsLoginResponse {
  authUrl?: string;
  loginUrl?: string;
  redirectUrl?: string;
  url?: string;
  message?: string;
  [key: string]: any;
}

export const getAllTeamsMeetings = async (): Promise<TeamsMeetingResponse[]> => {
  try {
    const response = await NestApi.get('teams-meetings/meetings');
    return unwrap(response.data);
  } catch (error: any) {
    console.error('Error fetching all Teams meetings:', error);
    throw error;
  }
};

export const getUpcomingWeekMeetings = async (): Promise<TeamsMeetingResponse[]> => {
  try {
    const response = await NestApi.get('teams-meetings/upcoming-week');
    return unwrap(response.data);
  } catch (error: any) {
    console.error('Error fetching upcoming week Teams meetings:', error);
    throw error;
  }
};

export const getTeamsMeetingById = async (meetingId: string): Promise<TeamsMeetingResponse> => {
  try {
    const response = await NestApi.get(`teams-meetings/meetings/${encodeURIComponent(meetingId)}`);
    return unwrap(response.data);
  } catch (error: any) {
    console.error('Error fetching Teams meeting by ID:', error);
    throw error;
  }
};

export const createTeamsMeetingInvite = async (payload: TeamsMeetingInvitePayload): Promise<any> => {
  try {
    const response = await NestApi.post('teams-meetings/invite', payload);
    return unwrap(response.data);
  } catch (error: any) {
    console.error('Error creating Teams meeting invite:', error);
    throw error;
  }
};

// OAuth Services
export const initiateTeamsLogin = async (): Promise<TeamsLoginResponse> => {
  try {
    const response = await NestApi.get('teams-meetings/oauth/login');
    return unwrap(response.data);
  } catch (error: any) {
    console.error('Error initiating Teams login:', error);
    throw error;
  }
};

/**
 * Kept for the slice's existing thunk. Azure redirects straight to the ATS callback now,
 * so the SPA never has a code to exchange; this only re-reads the connection status.
 */
export const handleOAuthCallback = async (_code: string, _state: string): Promise<any> => {
  const response = await NestApi.get('teams-meetings/oauth/status');
  return unwrap(response.data);
};

export const getTeamsAuthStatus = async (): Promise<TeamsStatusResponse> => {
  try {
    const response = await NestApi.get('teams-meetings/oauth/status');
    return unwrap(response.data);
  } catch (error: any) {
    console.error('Error fetching Teams auth status:', error);
    throw error;
  }
};